<template>
  <div class="pageContainer">
    <BannerComponent>
      <h6 class="banner-heading">Configured Port Forwards</h6>
    </BannerComponent>

    <button class="primaryButton" @click="toggleModal('Add', null)">Add Port Forward</button>
    <div
        class="card cardBackground"
        v-for="(port, i) of portForwards"
        :key="i"
    >
      <div class="portHeader">
        <div class="portGroup">
          <font-awesome-icon
              class="dropdownIcon"
              :class="port.dropdownOpen ? 'rotatedIcon' : null"
              :icon="['fas', 'chevron-right']"
              @click="toggleDropdown(port)"
          />
          <transition name="fade">
            <span v-if="!port.dropdownOpen" @click="toggleDropdown(port)">
              External Port: {{ port.externalPort }}
            </span>
          </transition>
        </div>
        <div class="portGroup">
          <font-awesome-icon
              class="editIcon"
              :icon="['fas', 'pen']"
              @click="toggleModal('Edit', port)"
          />
          <font-awesome-icon
              class="deleteIcon"
              :icon="['fas', 'trash-can']"
              @click="toggleModal('Delete', port)"
          />
        </div>
      </div>

      <!-- Wrap the conditional content in a <transition> -->
      <transition name="slide">
        <div class="portContent" v-if="port.dropdownOpen">
          <div class="portGroup">
              <span class="width50">
                External Port:<br />{{ port.externalPort }}
              </span>
            <span class="width50">
                Internal IP:<br />{{ port.internalIp }}
              </span>
          </div>
          <div class="portGroup">
              <span class="width50">
                Internal Port:<br />{{ port.internalPort }}
              </span>
            <span class="width50">
                Protocol:<br />{{ port.protocol }}
              </span>
          </div>
        </div>
      </transition>
    </div>
    <BaseModal
        :visible="modalOpen"
        :title="modalType === 'Delete' ? 'Are you sure?' : modalType + ' Port Forward'"
        @close="toggleModal(modalType, null)"
        @cancel="toggleModal(modalType, null)"
        @submit="onModalSubmit"
        :submit-label="submitLabel"
        :cancel-show="modalType === 'Delete'"
        :submit-color="modalType === 'Delete' ? 'var(--color-red-button)' : null"
        :submit-border-color="modalType === 'Delete' ? 'var(--color-red-button-border)' : null"
        :cancel-color="modalType === 'Delete' ? 'var(--color-primary-button)' : null"
        :cancel-border-color="modalType === 'Delete' ? 'var(--color-primary-button-border)' : null"
        :submit-disabled="checkSelectedPort"
    >
      <div v-if="modalType === 'Delete'">
        <span>Are you sure you want to delete the {{selectedPort.externalPort}} port forward rule?</span>
      </div>
      <div v-else class="modalContainer">
        <div class="modalLine">
          <div class="text-box-label" style="margin-top:.5rem;">
            External Port:
          </div>
          <input type="text" v-model="selectedPort.externalPort">
        </div>
        <div class="modalLine">
          <div class="text-box-label" style="margin-top:.5rem;">
            Internal IP:
          </div>
          <input type="text" v-model="selectedPort.internalIp">
        </div>
        <div class="modalLine">
          <div class="text-box-label" style="margin-top:.5rem;">
            Internal Port:
          </div>
          <input type="text" v-model="selectedPort.internalPort">
        </div>
        <div class="modalLine">
          <div class="text-box-label" style="margin-top:.5rem;">
            Protocol:
          </div>
          <CustomSelect
              v-model="selectedPort.protocol"
              :options="protocolOptions"
              :searchable="false"
          ></CustomSelect>
        </div>
      </div>
    </BaseModal>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import BaseModal from "@/components/BaseModal.vue";
import CustomSelect from "@/components/CustomSelect.vue"
import {useLoginStore} from "@/stores/loginStore";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import LoadingPage from "@/components/LoadingPage.vue";

const deviceAPIService = new DeviceAPIService()

export default {
  name: "PortForwardingPage",
  components: {
    BaseModal,
    BannerComponent,
    MessageModal,
    CustomSelect,
    LoadingPage
  },
  data() {
    return {
      loading: false,
      portForwards: [],
      appMessage: {
        text: '',
        type: 'error',
      },
      errorTxt: "",
      modalOpen: false,
      modalType: '',
      submitLabel: '',
      selectedPort: {
        externalPort: "",
        internalIp: "",
        internalPort: "",
        protocol: "",
        dropdownOpen: false,
      },
      protocolOptions: [
        {
          id: 'tcp',
          name: 'TCP',
        },
        {
          id: 'udp',
          name: 'UDP',
        },
      ]
    };
  },
  computed: {
    checkSelectedPort(){
      return !(this.selectedPort.externalPort && this.selectedPort.internalIp && this.selectedPort.internalPort && this.selectedPort.protocol)
    },
  },
  mounted(){
    //console.clear()
    // Only 'device' users can access port forwarding functionality
    if (useLoginStore().getSubscriptionType === 'device') {
      if (useLoginStore().getDeviceOnline) {
        this.getPortForwarding()
      } else {
        this.appMessage.text = "Device Offline"
        this.appMessage.type = 'info'
        this.$refs.messageModal.open()
      }
    }
    // 'free' users handled by route guards/subscription service
  },
  methods: {
  async onModalSubmit() {
    try {
      this.loading = true
      if (this.modalType === 'Add') {
        await deviceAPIService.postPortForwarding(
          {
            externalPort: this.selectedPort.externalPort,
            internalIp:    this.selectedPort.internalIp,
            internalPort:  this.selectedPort.internalPort,
            protocol:      this.selectedPort.protocol
          },
          this.$router,
          useLoginStore()
        )
        this.loading = false
        this.appMessage.text = 'Successfully added port forward!'
        this.appMessage.type = 'success'
        this.getPortForwarding()
      }
      else if (this.modalType === 'Edit') {
        await deviceAPIService.putPortForwarding(
          { /* whatever your API expects, e.g. id plus changed fields */ },
          this.$router,
          useLoginStore()
        )
        this.appMessage.text = 'Successfully updated port forward!'
        this.appMessage.type = 'success'
        this.getPortForwarding()
      }
      else if (this.modalType === 'Delete') {
        this.loading = true
        console.log({
          externalPort: this.selectedPort.externalPort,
          internalIp:    this.selectedPort.internalIp,
          internalPort:  this.selectedPort.internalPort,
          protocol:      this.selectedPort.protocol
        })
        await deviceAPIService.deletePortForwarding(
          {
            externalPort: this.selectedPort.externalPort,
            internalIp:    this.selectedPort.internalIp,
            internalPort:  this.selectedPort.internalPort,
            protocol:      this.selectedPort.protocol
          },
          this.$router,
          useLoginStore()
        )
        this.loading = false
        this.appMessage.text = 'Successfully removed port forward!'
        this.appMessage.type = 'success'
        this.getPortForwarding()
      }

      this.$refs.messageModal.open()
      await this.getPortForwarding()
      this.toggleModal(this.modalType, null)
    } catch (e) {
      this.showError(e)
      this.loading = false
    }
  },
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    toggleDropdown(port) {
      port.dropdownOpen = !port.dropdownOpen;
    },
    toggleModal(type, port) {
        this.modalType = type
        this.modalOpen = !this.modalOpen

        if (this.modalOpen) {
          // opening
          switch (type) {
            case 'Add':
              this.submitLabel = 'Add'
              this.selectedPort = {
                externalPort: '',
                internalIp: '',
                internalPort: '',
                protocol: this.protocolOptions[0].id,
                dropdownOpen: false,
              }
              break
            case 'Edit':
              this.submitLabel = 'Save'
              this.selectedPort = { ...port }
              break
            case 'Delete':
              this.submitLabel = 'Delete'
              this.selectedPort = { ...port }
              break
          }
        } else {
          // closing
          this.modalType = ''
          this.selectedPort = {
            externalPort: '',
            internalIp: '',
            internalPort: '',
            protocol: '',
            dropdownOpen: false,
          }
        }
      },
    async getPortForwarding() {
      try {
        this.loading = true
        const response = await deviceAPIService.getPortForwarding(this.$router, useLoginStore())
        this.portForwards = response.forwarders.map(entry => ({
          ...entry,
          dropdownOpen: false
        }))
        this.loading = false
      } catch (e) {
        this.showError(e)
        this.loading = true
      }
    },
    async deletePortForwarding(domain) {
      try {
        await deviceAPIService.deletePortForwarding({domain}, this.$router, useLoginStore())
        this.appMessage.text = 'Successfully removed port forward!'
        this.appMessage.type = 'success'
        this.$refs.messageModal.open()
        this.getPortForwarding()
      } catch (e) {
        this.showError(e)
        this.loading = true
      }
    },

  },
};
</script>

<style scoped>
.label{
  text-align: left;
}
.dropdownIcon {
  font-size: 20px;
  color: var(--text-primary);
  width: 20px;
  aspect-ratio: 1 / 1;
  transition: transform 0.3s ease;
}
.rotatedIcon {
  transform: rotate(90deg);
}

.portHeader {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.portContent {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.portGroup {
  display: flex;
  gap: 10px;
}
span {
  color: var(--text-primary);
  text-align: left;
}
.editIcon {
  font-size: 20px;
  color: var(--color-secondary-button);
}
.deleteIcon {
  font-size: 20px;
  color: var(--color-red-button);
}

/* slide-in/out animation */
.slide-enter-active,
.slide-leave-active {
  transition: all .3s ease;
}
.slide-enter {
  transform: translateY(-100%);
  opacity: 0;
}
.slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}
.slide-leave {
  transform: translateY(0);
  opacity: 1;
}
.slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-leave {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}
</style>
